import { ARCHETYPE_NAMES, PSYCHOTYPE_NAMES, SOCIONICS_NAMES } from '@upme/contracts';

/** Публичный словарь Кода личности. Internal-коды — ключи, наружу не светятся. */

export interface PersonalityNamed {
  name: string;
  description: string;
}

function invertLabelToCode(names: Record<string, string>): Readonly<Record<string, string>> {
  const out: Record<string, string> = {};
  for (const [code, label] of Object.entries(names)) {
    out[label.trim().toLowerCase()] = code;
  }
  return out;
}

/** 4 буквы дихотомий в начале строки (`ENTP`, `ENTP-A`). */
const MBTI_PREFIX = /^([EI][SN][TF][JP])/i;
/** `Аналитик (INTJ)` → INTJ. */
const MBTI_IN_PARENS = /\(([A-Za-z]{4})\)/;

/** 16 ядер: ключ — TIM (ILE…) или 4-буквенный код из скоринга (ENTP…). */
export const PERSONALITY_CORE_MAP: Record<string, PersonalityNamed> = {
  ILE: {
    name: 'Визионер',
    description: 'Видит возможности, соединяет идеи и замечает то, чего ещё нет',
  },
  SEI: {
    name: 'Гармонизатор',
    description: 'Держит атмосферу, считывает комфорт людей и возвращает равновесие',
  },
  ESE: {
    name: 'Катализатор',
    description: 'Зажигает пространство, собирает людей и превращает настроение в действие',
  },
  LII: {
    name: 'Архитектор',
    description: 'Строит ясные конструкции из хаоса идей и держит внутреннюю логику',
  },
  SLE: {
    name: 'Командир',
    description: 'Берёт поле, давит на результат и быстро превращает решение в движение',
  },
  IEI: {
    name: 'Провидец',
    description: 'Чувствует скрытый смысл момента и видит, куда всё это вызревает',
  },
  LIE: {
    name: 'Навигатор',
    description: 'Прокладывает курс, считает эффективность и тянет систему вперёд',
  },
  ESI: {
    name: 'Хранитель',
    description: 'Бережёт границы, близких и то, что нельзя потерять',
  },
  LSE: {
    name: 'Оператор',
    description: 'Держит процесс, доводит до рабочего состояния и не отпускает качество',
  },
  EII: {
    name: 'Медиатор',
    description: 'Слышит человека целиком и находит этичный путь между сторонами',
  },
  EIE: {
    name: 'Драматург',
    description: 'Собирает смысл в историю и ведёт за собой через эмоцию и образ',
  },
  LSI: {
    name: 'Стратег',
    description: 'Держит структуру, видит риски заранее и ставит порядок там, где он нужен',
  },
  SEE: {
    name: 'Драйвер',
    description: 'Входит в контакт, чувствует влияние и двигает людей к действию',
  },
  ILI: {
    name: 'Прогнозист',
    description: 'Считывает траектории, замечает слабые места и говорит, чем всё кончится',
  },
  IEE: {
    name: 'Коннектор',
    description: 'Открывает людей друг другу и видит, кто с кем сможет вспыхнуть',
  },
  SLI: {
    name: 'Инженер',
    description: 'Чувствует материал, удобство и то, как сделать так, чтобы работало',
  },
};

/**
 * 4-буквенный код из дихотомий теста → TIM. Без j/p-реверса: так совпадают
 * публичные имена ТЗ с тем, что уже лежит в колонке socionicsType.
 */
export const MBTI_TO_TIM: Readonly<Record<string, string>> = {
  ENTP: 'ILE',
  ISFP: 'SEI',
  ESFJ: 'ESE',
  INTJ: 'LII',
  ESTP: 'SLE',
  INFP: 'IEI',
  ENTJ: 'LIE',
  ISFJ: 'ESI',
  ESTJ: 'LSE',
  INFJ: 'EII',
  ENFJ: 'EIE',
  ISTJ: 'LSI',
  ESFP: 'SEE',
  INTP: 'ILI',
  ENFP: 'IEE',
  ISTP: 'SLI',
};

export const RADICAL_MAP: Record<string, PersonalityNamed> = {
  hysteroid: { name: 'Сцена', description: 'видимость, внимание, драма момента' },
  epileptoid: { name: 'Структура', description: 'порядок, рамка, контроль' },
  paranoid: { name: 'Вектор', description: 'цель, идея, настойчивое направление' },
  schizoid: { name: 'Глубина', description: 'дистанция, анализ, внутреннее пространство' },
  hyperthymic: { name: 'Импульс', description: 'энергия, скорость, лёгкий старт' },
  emotive: { name: 'Резонанс', description: 'чувство, отклик, тонкая связь' },
  anxious: { name: 'Чуткость', description: 'осторожность, безопасность, предчувствие риска' },
};

/** Легаси-строки радикалов (колонка/JSON с русским именем, не кодом). */
export const RADICAL_ALIAS: Readonly<Record<string, string>> = invertLabelToCode(PSYCHOTYPE_NAMES);

/** Легаси-строки образов (`Мудрец`, `Искатель`…) → slug архетипа. */
export const ARCHETYPE_ALIAS: Readonly<Record<string, string>> = invertLabelToCode(ARCHETYPE_NAMES);

const SOCIONICS_LABEL_TO_CODE: Readonly<Record<string, string>> = invertLabelToCode(SOCIONICS_NAMES);

export const ARCHETYPE_MAP: Record<string, PersonalityNamed> = {
  caregiver: { name: 'Покровитель', description: 'забота, защита, поддержка' },
  creator: { name: 'Создатель', description: 'создание, воображение, новое' },
  hero: { name: 'Преодолевающий', description: 'смелость, преодоление, достижение' },
  sage: { name: 'Познающий', description: 'знание, понимание, истина' },
  explorer: { name: 'Исследователь', description: 'свобода, путь, открытия' },
  lover: { name: 'Связующий', description: 'близость, красота, связь' },
  ruler: { name: 'Ведущий', description: 'ответственность, порядок, влияние' },
  innocent: { name: 'Светлый', description: 'доверие, безопасность, оптимизм' },
};

/**
 * @internal Экспортирован только ради внутрилибных импортов (например,
 * `resolvePersonalityCore` в этом же файле). Публичный вход для резолва ТИМа
 * из соционики — `resolveSocionicsCode` (`socionics/types.ts`), у него другой
 * набор форматов и строгий тип `TimCode` на выходе.
 */
export function canonicalTim(internalCode: string): string | null {
  const raw = internalCode.trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (PERSONALITY_CORE_MAP[upper]) return upper;
  const fromMbti = MBTI_TO_TIM[upper];
  if (fromMbti) return fromMbti;
  const fromLabel = SOCIONICS_LABEL_TO_CODE[raw.toLowerCase()];
  if (fromLabel) {
    const tim = MBTI_TO_TIM[fromLabel.toUpperCase()];
    return tim ?? null;
  }
  const inParens = MBTI_IN_PARENS.exec(raw)?.[1]?.toUpperCase();
  if (inParens) {
    const tim = MBTI_TO_TIM[inParens];
    if (tim) return tim;
  }
  const prefix = MBTI_PREFIX.exec(upper)?.[1]?.toUpperCase();
  if (prefix) return MBTI_TO_TIM[prefix] ?? null;
  return null;
}

export function resolvePersonalityCore(internalCode: string): PersonalityNamed | null {
  const tim = canonicalTim(internalCode);
  return tim ? (PERSONALITY_CORE_MAP[tim] ?? null) : null;
}

export function resolveRadicalCode(internal: string): string | null {
  const raw = internal.trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (RADICAL_MAP[lower]) return lower;
  return RADICAL_ALIAS[lower] ?? null;
}

export function resolvePersonalityImage(internalCode: string): PersonalityNamed | null {
  const raw = internalCode.trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  const slug = ARCHETYPE_MAP[lower] ? lower : (ARCHETYPE_ALIAS[lower] ?? null);
  return slug ? (ARCHETYPE_MAP[slug] ?? null) : null;
}

/** Голоса ритма: ключи могут быть кодами или русскими именами радикалов. */
export function normalizeRadicalCounts(counts: Readonly<Record<string, number>>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, n] of Object.entries(counts)) {
    if (!Number.isFinite(n)) continue;
    const code = resolveRadicalCode(key);
    if (!code) continue;
    out[code] = (out[code] ?? 0) + n;
  }
  return out;
}

/** Семь ответов радикала; null, если хоть один не узнаётся (тогда ритм из counts/доминанты). */
export function normalizeRadicalAnswers(answers: ReadonlyArray<string>): string[] | null {
  const codes = answers.map((item) => resolveRadicalCode(item));
  if (!codes.every((code): code is string => code !== null)) return null;
  return codes;
}
